import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Upload, CheckCircle2, PiggyBank, Info, CreditCard, Banknote, AlertCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { formatRupiah, koperasiInfo } from "@/lib/dummyData";
import { useAuth } from "@/lib/auth";
import { SavingsAPI, UploadAPI } from "@/lib/api";
import { getMidtransConfig, openSnap } from "@/lib/midtrans";

// Baca file gambar menjadi data URL base64 untuk diunggah ke backend.
const fileToDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

// Info per jenis simpanan (jenis dari URL: wajib | sukarela).
const JENIS_INFO = {
  wajib: {
    label: "Simpanan Wajib",
    nominalTetap: 100000,
    deskripsi:
      "Simpanan Wajib adalah kewajiban rutin setiap anggota sebesar Rp 100.000 per bulan. " +
      "Simpanan ini menjadi bagian dari modal koperasi dan ikut diperhitungkan dalam pembagian SHU. " +
      "Simpanan Wajib tidak dapat ditarik selama masih menjadi anggota.",
    poin: [
      "Nominal tetap Rp 100.000 per bulan.",
      "Dibayarkan paling lambat tanggal 10 setiap bulan.",
      "Ikut diperhitungkan dalam pembagian SHU tahunan.",
      "Tidak dapat ditarik selama status keanggotaan aktif.",
    ],
  },
  sukarela: {
    label: "Simpanan Sukarela",
    nominalTetap: null,
    deskripsi:
      "Simpanan Sukarela adalah tabungan bebas milik anggota — nominal setoran bebas dan " +
      "dapat ditarik kembali sesuai kebutuhan melalui pengajuan penarikan yang diverifikasi Admin Keuangan.",
    poin: [
      "Nominal setoran bebas (minimal Rp 10.000).",
      "Dapat disetor kapan saja.",
      "Dapat ditarik kembali melalui menu Tarik Simpanan Sukarela.",
      "Tidak mengurangi hak SHU dari simpanan pokok & wajib.",
    ],
  },
};

export default function SavingsDeposit() {
  const { jenis } = useParams(); // "wajib" | "sukarela"
  const info = JENIS_INFO[jenis];
  const { user } = useAuth();
  const navigate = useNavigate();

  const [amount, setAmount] = useState(info?.nominalTetap || "");
  const [senderName, setSenderName] = useState("");
  const [bankName, setBankName] = useState("BCA");
  const [proofFile, setProofFile] = useState(null);
  const [sending, setSending] = useState(false);

  // Metode setoran: Midtrans (bila aktif) atau transfer manual (bila rekening dikonfigurasi).
  const manualEnabled = !!koperasiInfo.bankAccount;
  const [midtransEnabled, setMidtransEnabled] = useState(false);
  const [cfgLoaded, setCfgLoaded] = useState(false);
  const [method, setMethod] = useState(manualEnabled ? "manual" : null);

  useEffect(() => { if (user?.name) setSenderName(user.name); }, [user]);
  useEffect(() => {
    getMidtransConfig().then((cfg) => {
      if (cfg.enabled) { setMidtransEnabled(true); setMethod("midtrans"); }
      setCfgLoaded(true);
    });
  }, []);

  if (!info) {
    return (
      <div className="text-sm text-ink/75">
        Jenis simpanan tidak dikenal. <Link to="/anggota/tabungan" className="text-forest underline">Kembali ke Tabungan</Link>
      </div>
    );
  }

  const validNominal = () => {
    const nominal = Number(amount);
    if (!nominal || nominal <= 0) { toast.error("Isi nominal setoran yang benar"); return null; }
    if (jenis === "sukarela" && nominal < 10000) { toast.error("Minimal setoran sukarela Rp 10.000"); return null; }
    return nominal;
  };

  // Setor lewat Midtrans: buka Snap, lalu cek status. Saldo bertambah otomatis setelah
  // Midtrans mengonfirmasi pembayaran (untuk VA/QRIS bisa setelah popup ditutup).
  const payOnline = async () => {
    const nominal = validNominal();
    if (!nominal) return;
    setSending(true);
    try {
      const { payment, deposit } = await SavingsAPI.depositMidtrans(jenis.toUpperCase(), nominal);
      const result = await openSnap(payment.token);
      const latest = await SavingsAPI.depositMidtransSync(deposit.id).catch(() => null);
      if (latest?.status === "PAID") {
        toast.success(`${info.label} ${formatRupiah(latest.amount)} berhasil disetor`);
        setTimeout(() => navigate("/anggota/tabungan"), 900);
      } else if (latest?.midtrans_status === "pending" || result === "pending") {
        toast.info("Menunggu pembayaran — saldo bertambah otomatis setelah pembayaran diterima.", { duration: 7000 });
        setTimeout(() => navigate("/anggota/tabungan"), 1500);
      } else if (result === "error" || latest?.status === "FAILED") {
        toast.error("Pembayaran gagal. Silakan coba lagi.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Gagal membuka pembayaran");
    } finally {
      setSending(false);
    }
  };

  const submit = async () => {
    const nominal = validNominal();
    if (!nominal) return;
    if (!proofFile) return toast.error("Unggah bukti transfer dulu");
    setSending(true);
    try {
      const dataUrl = await fileToDataUrl(proofFile);
      const url = await UploadAPI.image(proofFile.name, dataUrl);
      await SavingsAPI.depositProof({
        type: jenis.toUpperCase(),           // WAJIB | SUKARELA
        amount: nominal,
        bank_name: bankName,
        sender_name: senderName,
        proof_url: url,
      });
      toast.success("Bukti setoran terkirim — menunggu verifikasi Admin Keuangan.");
      setTimeout(() => navigate("/anggota/tabungan"), 900);
    } catch (err) {
      toast.error(err?.response?.data?.error || "Gagal mengirim bukti setoran");
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <Link to="/anggota/tabungan" className="inline-flex items-center gap-1.5 text-sm text-forest hover:underline">
          <ArrowLeft size={15} /> Kembali ke Buku Tabungan
        </Link>
        <h1 className="font-display text-3xl font-bold text-ink tracking-tight mt-3">Setor {info.label}</h1>
        <p className="text-ink/75 mt-2 text-sm">Baca informasi berikut, lalu bayar online atau transfer manual.</p>
      </div>

      {/* Informasi simpanan */}
      <Card className="rounded-3xl border-stone-soft p-6">
        <div className="flex items-center gap-3">
          <span className="w-11 h-11 rounded-xl grid place-items-center bg-forest/10"><PiggyBank size={18} className="text-forest" /></span>
          <div>
            <div className="font-display font-semibold text-ink">{info.label}</div>
            {info.nominalTetap
              ? <Badge className="bg-amber-warm/20 text-amber-700 border-0 rounded-full mt-1">{formatRupiah(info.nominalTetap)} / bulan</Badge>
              : <Badge className="bg-sage/20 text-forest border-0 rounded-full mt-1">Nominal bebas</Badge>}
          </div>
        </div>
        <p className="text-sm text-ink/75 mt-4">{info.deskripsi}</p>
        <ul className="mt-4 space-y-2">
          {info.poin.map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-ink/80">
              <Info size={14} className="text-forest mt-0.5 shrink-0" /> {p}
            </li>
          ))}
        </ul>
      </Card>

      {/* Metode setoran */}
      {midtransEnabled && manualEnabled && (
        <div className="grid sm:grid-cols-2 gap-3">
          {[
            { v: "midtrans", icon: CreditCard, l: "Bayar Online (Midtrans)", d: "VA, QRIS, e-wallet, kartu — tercatat otomatis" },
            { v: "manual", icon: Banknote, l: "Transfer Manual", d: "Unggah bukti, diverifikasi Admin Keuangan" },
          ].map((m) => (
            <button
              key={m.v}
              type="button"
              onClick={() => setMethod(m.v)}
              className={`flex items-start gap-3 text-left border rounded-2xl p-4 transition ${method === m.v ? "border-forest bg-forest/5" : "border-stone-soft bg-white hover:bg-cream"}`}
              data-testid={`deposit-method-${m.v}`}
            >
              <m.icon size={18} className="text-forest mt-0.5 shrink-0" />
              <span>
                <span className="block text-sm font-semibold text-ink">{m.l}</span>
                <span className="block text-xs text-ink/65 mt-0.5">{m.d}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {cfgLoaded && !method && (
        <div className="flex items-start gap-2 rounded-2xl bg-terracotta/10 text-terracotta text-sm p-4" data-testid="deposit-none">
          <AlertCircle size={16} className="shrink-0 mt-0.5" />
          Pembayaran online belum aktif. Silakan hubungi pengurus koperasi.
        </div>
      )}

      {method === "midtrans" && (
        <Card className="rounded-3xl border-stone-soft p-6">
          <h3 className="font-display font-semibold text-ink mb-4">Bayar Online</h3>
          <div className="space-y-1.5 max-w-xs">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Nominal Setoran</Label>
            <Input
              type="number"
              inputMode="numeric"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={Boolean(info.nominalTetap)}
              className="bg-cream border-stone-soft rounded-lg"
              data-testid="deposit-amount-online"
            />
            {info.nominalTetap && <p className="text-[11px] text-ink/55">Nominal Simpanan Wajib bersifat tetap.</p>}
          </div>
          <div className="flex gap-3 mt-6">
            <Button variant="outline" className="rounded-full flex-1" onClick={() => navigate("/anggota/tabungan")}>Batal</Button>
            <Button disabled={sending} className="rounded-full flex-1 bg-forest hover:bg-forest-dark text-white" onClick={payOnline} data-testid="deposit-pay-online">
              <CreditCard size={16} /> {sending ? "Membuka…" : `Bayar ${Number(amount) > 0 ? formatRupiah(Number(amount)) : ""}`}
            </Button>
          </div>
          <p className="text-[11px] text-ink/55 mt-3">
            Halaman pembayaran Midtrans akan terbuka (Virtual Account, QRIS, e-wallet, kartu). Setoran tercatat
            di Buku Tabungan otomatis setelah pembayaran diterima.
          </p>
        </Card>
      )}

      {method === "manual" && (<>
      {/* Rekening tujuan */}
      <Card className="rounded-3xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink mb-3">Rekening Tujuan Transfer</h3>
        <div className="rounded-2xl bg-cream p-5 text-sm space-y-1">
          <div className="flex justify-between"><span className="text-ink/75">Bank Tujuan</span><span className="font-semibold text-ink">{koperasiInfo.bankName} {koperasiInfo.bankAccount}</span></div>
          <div className="flex justify-between"><span className="text-ink/75">Atas Nama</span><span className="font-semibold text-ink">{koperasiInfo.bankHolder}</span></div>
          <div className="flex justify-between"><span className="text-ink/75">Berita Transfer</span><span className="font-semibold text-ink">{info.label} — {user?.koperasi_member_number || user?.name || ""}</span></div>
        </div>
      </Card>

      {/* Form setoran + upload bukti */}
      <Card className="rounded-3xl border-stone-soft p-6">
        <h3 className="font-display font-semibold text-ink mb-4">Konfirmasi Setoran</h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Nominal Setoran</Label>
            <Input
              type="number"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              disabled={Boolean(info.nominalTetap)}
              className="bg-cream border-stone-soft rounded-lg"
              data-testid="deposit-amount"
            />
            {info.nominalTetap && <p className="text-[11px] text-ink/55">Nominal Simpanan Wajib bersifat tetap.</p>}
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Nama Pengirim</Label>
            <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} className="bg-cream border-stone-soft rounded-lg" data-testid="deposit-sender" />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label className="text-xs text-ink/55 uppercase tracking-wider">Bank Pengirim</Label>
            <Input value={bankName} onChange={(e) => setBankName(e.target.value)} className="bg-cream border-stone-soft rounded-lg" data-testid="deposit-bank" />
          </div>
        </div>

        <label htmlFor="savings-proof" className="mt-5 block border-2 border-dashed border-stone-soft rounded-2xl p-6 text-center cursor-pointer hover:bg-cream transition">
          <Upload size={22} className="mx-auto text-forest" />
          <div className="font-semibold text-sm text-ink mt-2">{proofFile ? proofFile.name : "Klik untuk pilih bukti transfer"}</div>
          <div className="text-xs text-ink/55 mt-1">JPG/PNG/WEBP, maks. 700 KB</div>
          <input id="savings-proof" type="file" accept="image/*" className="hidden" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
        </label>

        <div className="flex gap-3 mt-6">
          <Button variant="outline" className="rounded-full flex-1" onClick={() => navigate("/anggota/tabungan")}>Batal</Button>
          <Button disabled={sending} className="rounded-full flex-1 bg-forest hover:bg-forest-dark text-white" onClick={submit} data-testid="deposit-submit">
            <CheckCircle2 size={16} /> {sending ? "Mengirim…" : "Kirim Bukti Setoran"}
          </Button>
        </div>
        <p className="text-[11px] text-ink/55 mt-3">
          Setoran akan tercatat di Buku Tabungan setelah bukti diverifikasi oleh Admin Keuangan.
        </p>
      </Card>
      </>)}
    </div>
  );
}
